import { backEndBaseURL } from './settings';

export const CLINIC_STORAGE_KEYS = {
  TOKEN: 'BUYER_TOKEN',
  REFRESH_TOKEN: 'BUYER_REFRESH_TOKEN',
  USER: 'BUYER_USER',
  GUEST_CART: 'BUYER_GUEST_CART',
  GUEST_ORDERS: 'BUYER_GUEST_ORDERS',
} as const;

export const USE_MOCK = true;

/**
 * Per-module mock override. When `false`, that module's service hits the real backend.
 * Used for incremental USE_MOCK → real backend migration; flip one module at a time.
 */
export const USE_MOCK_AUTH = false;
export const USE_MOCK_PROFILE = false;
export const USE_MOCK_PRODUCTS = false;
export const USE_MOCK_PUBLIC_CATALOG = false;
export const USE_MOCK_BUSINESS = false;
export const USE_MOCK_INVITATION = false;
export const USE_MOCK_CART = false;
export const USE_MOCK_ORDERS = false;
export const USE_MOCK_PAYMENTS = false;
export const USE_MOCK_NOTIFICATIONS = false;
export const USE_MOCK_SUPPLIER_BRAND = false;

// Backend API base. Mirrors the supplier portal's environment switching
// (`settings.ts → backEndBaseURL`): prod build → production, staging build →
// demo, and local `gatsby develop` → the dev backend (NOT production).
// `GATSBY_API_BASE_URL` still wins when set, e.g.
// `GATSBY_API_BASE_URL=http://localhost:5127/api/v1 gatsby develop`.
export const API_BASE_URL_CLINIC =
  process.env.GATSBY_API_BASE_URL || `${backEndBaseURL}/api/v1`;

/**
 * Physical-goods minimum order subtotal (AUD). Orders whose physical subtotal
 * meets or exceeds this threshold ship free; below it, checkout is blocked.
 * Virtual-only carts are exempt from both rules.
 */
export const MIN_PHYSICAL_ORDER_AUD = 150;
export const FLAT_FREIGHT_FEE_AUD = 12;

// Square config lives in `src/constants/settings.ts` (committed, sandbox-safe).
// Re-export so existing `from 'buyer-portal'` imports keep working.
export {
  SQUARE_APP_ID,
  SQUARE_ENVIRONMENT,
  SQUARE_SDK_URL,
} from './settings';

export type BuyerNavGroup = 'workspace' | 'records' | 'account' | 'business';

export type BuyerNavItem = {
  key: string;
  labelKey: string;
  path: string;
  group: BuyerNavGroup;
  icon: string;
  guestVisible?: boolean;
  guestOnly?: boolean;
  exact?: boolean;
  /** When `true`, only render this item if the user has an ACTIVE BusinessMembership. */
  businessOnly?: boolean;
};

export const BUYER_NAV_GROUPS: { key: BuyerNavGroup; labelKey: string }[] = [
  { key: 'workspace', labelKey: 'BUYER_NAV_GROUP_WORKSPACE' },
  { key: 'records', labelKey: 'BUYER_NAV_GROUP_RECORDS' },
  { key: 'business', labelKey: 'BUYER_NAV_GROUP_BUSINESS' },
  { key: 'account', labelKey: 'BUYER_NAV_GROUP_ACCOUNT' },
];

export const BUYER_NAV: BuyerNavItem[] = [
  { key: 'products', labelKey: 'BUYER_NAV_CATALOGUE', path: '/products', group: 'workspace', icon: 'catalogue', guestVisible: true },
  { key: 'suppliers', labelKey: 'BUYER_NAV_SUPPLIERS', path: '/suppliers', group: 'workspace', icon: 'profile', guestVisible: true, exact: true },
  { key: 'cart', labelKey: 'BUYER_NAV_CART', path: '/cart', group: 'workspace', icon: 'cart', guestVisible: true },
  { key: 'dashboard', labelKey: 'BUYER_NAV_DASHBOARD', path: '/dashboard', group: 'workspace', icon: 'home', exact: true },
  { key: 'orders', labelKey: 'BUYER_NAV_ORDERS', path: '/orders', group: 'records', icon: 'orders' },
  {
    key: 'orders-lookup',
    labelKey: 'BUYER_NAV_TRACK_ORDER',
    path: '/orders/lookup',
    group: 'records',
    icon: 'orders',
    guestVisible: true,
    guestOnly: true,
  },
  { key: 'payments', labelKey: 'BUYER_NAV_PAYMENTS', path: '/payments', group: 'records', icon: 'payments' },
  { key: 'business', labelKey: 'BUYER_NAV_BUSINESS', path: '/business', group: 'business', icon: 'profile', exact: true, businessOnly: true },
  { key: 'profile', labelKey: 'BUYER_NAV_PROFILE', path: '/profile', group: 'account', icon: 'profile', exact: true },
  { key: 'notifications', labelKey: 'BUYER_NAV_NOTIFICATIONS', path: '/notifications', group: 'account', icon: 'bell' },
];

// Backward-compat aliases retained during the V2.2 migration. Prefer BUYER_* in new code.
export type ClinicNavGroup = BuyerNavGroup;
export type ClinicNavItem = BuyerNavItem;
export const CLINIC_NAV_GROUPS = BUYER_NAV_GROUPS;
export const CLINIC_NAV = BUYER_NAV;

export const CLINIC_LANDING_PATH = '/products';
export const CLINIC_GUEST_LANDING_PATH = '/products';
export const CLINIC_LOGIN_PATH = '/login';
export const CLINIC_REGISTER_PATH = '/register';
export const CLINIC_INACTIVE_PATH = '/account-inactive';
export const CLINIC_FORBIDDEN_PATH = '/forbidden';
export const CLINIC_LOOKUP_PATH = '/orders/lookup';

export const BUYER_LANDING_PATH = CLINIC_LANDING_PATH;
export const BUYER_GUEST_LANDING_PATH = CLINIC_GUEST_LANDING_PATH;
export const BUYER_LOGIN_PATH = CLINIC_LOGIN_PATH;
export const BUYER_REGISTER_PATH = CLINIC_REGISTER_PATH;
export const BUYER_INACTIVE_PATH = CLINIC_INACTIVE_PATH;
export const BUYER_FORBIDDEN_PATH = CLINIC_FORBIDDEN_PATH;
export const BUYER_LOOKUP_PATH = CLINIC_LOOKUP_PATH;
export const BUYER_ONBOARDING_PATH = '/onboarding';
export const BUYER_BUSINESS_PATH = '/business';
export const BUYER_INVITATION_PATH = '/invitations';
